<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="row">
        <div class="col-md-12">
          <h1 class="ts-py-2">{{ $t('subjectGradingRange.academic') }}</h1>
          <div class="ts-border ts-rounded ts-border-gray-200 ts-border-opacity-20 ts-p-3">
            <div class="row ts-py-2">
              <div class="col-md-6">
                <label class="required ts-text-xs">
                  {{ $t('subjectGradingRange.mainProgram') }}
                </label>
                <a-select v-model.number="model.acp_id" :options="main_programs"
                  @change="onMainProgramChange(model.acp_id)" style="width:100%"></a-select>
              </div>
              <div class="col-md-6">
                <label class="required ts-text-xs">
                  {{ $t('subjectGradingRange.subProgram') }}
                </label>
                <a-select v-model.number="model.acps_id" :options="sub_programs"
                  @change="onSubProgramChange(model.acps_id)" style="width:100%"></a-select>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <label class="required ts-text-xs">
                  {{ $t('subjectGradingRange.level') }}
                </label>
                <a-select v-model.number="model.level_id" :options="levels" @change="onLevelChange(model.level_id)"
                  style="width:100%"></a-select>
              </div>
              <div class="col-md-6">
                <label class="required ts-text-xs">
                  {{ $t('subjectGradingRange.grade') }}
                </label>
                <a-select v-model.number="model.grade_id" :options="grades" style="width:100%"
                  @change="onGradeChange(model.grade_id)"></a-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
export default {
  name: 'academic',
  props: ['value'],
  data() {
    return {
      main_programs: [],
      sub_programs: [],
      levels: [],
      grades: [],
      subjects: [],
      grade_definitions: [],
      loading: false,
      model: {}
    }
  },
  created() {
    this.model = this.value
    this.fetchMainProgram()
  },
  methods: {
    ...mapActions('subjectGradingRange', [
      'getSubProgram',
      'getLevel',
      'getGrade',
      'getSubject',
      'getGradingDefinition'
    ]),
    fetchMainProgram() {
      this.main_programs = []
      this.loading = true
      this.$store
        .dispatch('subjectGradingRange/getMainProgram')
        .then(response => {
          this.main_programs = response.data.map(el => ({
            value: el.acp_id,
            label: el.acp_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async onMainProgramChange(acp_id) {
      if (!acp_id) return;

      this.model.acps_id = null
      this.model.level_id = null
      this.model.grade_id = null
      this.model.subject_score_grading_list = []
      this.sub_programs = []
      this.levels = []
      this.grades = []
      this.subjects = []
      this.grade_definitions = []

      // ✅ Emit cleared subjects immediately
      this.$emit('result', {
        gradeDefinitions: [],
        subjects: []
      })

      this.loading = true
      try {
        const sub_program_response = await this.getSubProgram(acp_id)
        const grade_definition_response = await this.getGradingDefinition(acp_id)

        this.sub_programs = sub_program_response.data.map(el => ({
          value: el.acps_id,
          label: el.acps_name_foreign
        }))
        this.grade_definitions = grade_definition_response.data.map(el => ({
          value: el.grade_definition_id,
          label: el.grade,
          definition_type: el.definition_type
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.loading = false
      }
    },

    onSubProgramChange(acps_id) {
      if (!acps_id) {
        return
      }
      this.model.level_id = null
      this.model.grade_id = null
      this.levels = []
      this.grades = []
      this.subjects = []
      this.loading = true
      this.getLevel(acps_id)
        .then(response => {
          this.levels = response.data.map(el => ({
            value: el.level_id,
            label: el.level_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onLevelChange(level_id) {
      if (!level_id) {
        return
      }
      this.model.grade_id = null
      this.grades = []
      this.subjects = []
      this.loading = true
      this.getGrade(level_id)
        .then(response => {
          this.grades = response.data.map(el => ({
            value: el.grade_id,
            label: el.grade_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onGradeChange(grade_id) {
      if (!grade_id) {
        return
      }
      this.loading = true
      this.getSubject(grade_id)
        .then(response => {
          if (response.data[0].grade_subjects != null) {
            this.subjects = response.data[0].grade_subjects.map(el => ({
              value: el.grade_subject_id,
              label: el.subject_name_foreign,
              subject_id: el.subject_id
            }))
          }
          this.$emit('result', {
            gradeDefinitions: this.grade_definitions,
            subjects: this.subjects
          })
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    subjects: {
      handler(newSubjects) {
        if (!newSubjects || newSubjects.length === 0) {
          // ✅ Clear when parent resets subjects
          this.selectedSubject = []
          this.model.subject_score_grading_list = []
        }
      },
      deep: true
    },
    gradeDefinitions: {
      handler() {
        // ✅ Guard to avoid error during component creation
        if (typeof this.createItem === 'function') {
          this.createItem()
        }
      },
      deep: true,
      immediate: true // ← correct spelling
    },
    validate(error) {
      this.errors = new Errors(error.errors)
    }
  }


}

</script>
